import { z } from 'zod';

import { aiStatusSchema } from '../ai';
import { brandSchema } from '../brand';
import { contentCreateSchema, contentSchema } from '../content';
import { contentAnalysisCreateSchema, contentAnalysisSchema } from '../content-analysis';
import { ContentAiFunnelStatus, ContentType, contentFunnelSchema } from '../content-funnel';
import { ExportKind } from '../export';
import { IntegrationProvider } from '../organization-integration';
import { seoResSchema } from '../seo';
import { defaultNullish, mongoIdSchema } from '../shared';

const articleSortAndFilter = z.object({
  createdBy: z.array(z.string()).nullish(),
  search: z.string().nullish(),
  offset: z.coerce.number().nullish(),
  limit: z.coerce.number().nullish(),
  sortBy: z
    .enum([
      'createdAt',
      'updatedAt',
      'createdBy',
      'seoScore',
      'status',
      'userStatus',
      'publicationDate',
      'topic',
      'primaryKeyword',
      'title',
      'language',
      'country',
    ] as const)
    .nullish(),
  sortByDir: z.enum(['ASC', 'DESC']).nullish(),
});
export type ArticleSortAndFilter = z.infer<typeof articleSortAndFilter>;

//////////////////////////
// Content get socket event

const articleSocketRes = z.object({
  seoScore: seoResSchema,
  analysis: contentAnalysisSchema.nullish(),
  content: contentSchema,
  funnel: z
    .object({
      _id: contentFunnelSchema.shape._id,
      createdAt: z.number(),
      updatedAt: z.number(),

      contentId: contentSchema.shape._id,

      selectedTitle: z.string().trim(),
      selectedOutline: z.array(
        z.object({
          text: z.string(),
          tag: z.string(),
          instructions: z.array(z.string()),
        })
      ),
      selectedVoice: contentFunnelSchema.shape.selectedVoice,
      selectedMetaTitle: z.string().trim(),
      customizedAiTitles: defaultNullish(z.array(z.string())).default([]),

      // this data comes from the last step in the funnel, the generation
      generatedMetaDescription: z.string(),
      generatedAlertSize: z.boolean(),
      generatedContent: z.string(),

      aiTitles: z.array(z.string()).nullish(),
      aiOutlines: z.array(
        z.object({
          text: z.string(),
          tag: z.string(),
          instructions: z.array(z.string()),
        })
      ),

      contentType: z.nativeEnum(ContentType).nullish(),
      talkAboutBrandInIntro: z.boolean().nullish(),
      talkAboutBrandInBody: z.boolean().nullish(),
      talkAboutBrandInConclusion: z.boolean().nullish(),
      supportingMaterials: z
        .array(
          z.object({
            url: z.string(),
            text: z.string(),
          })
        )
        .nullish(),
      customWordCount: z.boolean().nullish(),
      customWordCountValue: z.number().nullish(),

      status: z.enum([
        ContentAiFunnelStatus.Brief,
        ContentAiFunnelStatus.ReqGenTitles,
        ContentAiFunnelStatus.ReqGenTitlesLoad,
        ContentAiFunnelStatus.ReqGenTitlesError,

        ContentAiFunnelStatus.ReqGenOutlines,
        ContentAiFunnelStatus.ReqGenOutlinesLoad,
        ContentAiFunnelStatus.ReqGenOutlinesError,

        ContentAiFunnelStatus.ReqGenContent,
        ContentAiFunnelStatus.ReqGenContentLoad,
        ContentAiFunnelStatus.ReqGenContentError,

        ContentAiFunnelStatus.Done,
      ]),
    })
    .nullish(),
});
export const getArticleSocketResSchema = z.object({
  list: z.array(articleSocketRes),
  errors: z.array(
    z.object({
      contentId: z.string(),
      errorMsg: z.string(),
      errorCode: z.number(),
    })
  ),
  listDeleted: z.array(z.string()),
});
export type GetArticleSocketRes = z.infer<typeof getArticleSocketResSchema>;

//////////////////////////
// GetByOrgId
export const getArticlesByOrgIdReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape.organizationId,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetArticlesByOrgIdReq = z.infer<typeof getArticlesByOrgIdReqSchema>;

export const getArticlesByOrgIdResSchema = {
  200: z.object({
    data: z.object({
      contents: z.array(contentSchema),
      funnels: z.array(contentFunnelSchema),
    }),
    totalCount: z.number(),
  }),
};
export type GetArticlesByOrgIdRes = z.infer<(typeof getArticlesByOrgIdResSchema)[200]>;

//////////////////////////
// GetByBrandId
export const getArticlesByBrandIdReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape.brandId,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetArticlesByBrandIdReq = z.infer<typeof getArticlesByBrandIdReqSchema>;

export const getArticlesByBrandIdResSchema = {
  200: z.object({
    totalCount: z.number(),
    data: z.object({
      contents: z.array(contentSchema),
      funnels: z.array(contentFunnelSchema),
    }),
  }),
};
export type GetArticlesByBrandIdRes = z.infer<(typeof getArticlesByBrandIdResSchema)[200]>;

//////////////////////////
// Create
export const createArticleReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: contentCreateSchema.strip(),
});
export type CreateArticleReq = z.infer<typeof createArticleReqSchema>;

export const createArticleResSchema = {
  200: z.object({
    data: contentSchema.strip(),
  }),
};
export type CreateArticleRes = z.infer<(typeof createArticleResSchema)[200]>;

//////////////////////////
// GetById
export const getArticleByIdReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetArticleByIdReq = z.infer<typeof getArticleByIdReqSchema>;

export const getArticleByIdResSchema = {
  200: z.object({
    data: z.object({
      analysis: contentAnalysisSchema.nullish(),
      content: contentSchema,
      funnel: contentFunnelSchema.nullish(),
      seoScore: seoResSchema,
    }),
  }),
};
export type GetArticleByIdRes = z.infer<(typeof getArticleByIdResSchema)[200]>;

//////////////////////////
// Save
export const saveArticleReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: contentCreateSchema.partial().strip(),
});
export type SaveArticleReq = z.infer<typeof saveArticleReqSchema>;

export const saveArticleResSchema = {
  200: z.object({
    data: z.object({
      analysis: contentAnalysisSchema.nullish(),
      content: contentSchema,
      funnel: contentFunnelSchema.nullish(),
      seoScore: seoResSchema,
    }),
  }),
};
export type SaveArticleRes = z.infer<(typeof saveArticleResSchema)[200]>;

//////////////////////////
// Delete
export const delArticleReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type DelArticleReq = z.infer<typeof delArticleReqSchema>;

export const delArticleResSchema = {
  200: z.object({}),
};
export type DelArticleRes = z.infer<(typeof delArticleResSchema)[200]>;

//////////////////////////
// Save analysis
export const saveArticleAnalysisReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: contentAnalysisCreateSchema.partial(),
});
export type SaveArticleAnalysisReq = z.infer<typeof saveArticleAnalysisReqSchema>;

export const saveArticleAnalysisResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type SaveArticleAnalysisRes = z.infer<(typeof saveArticleAnalysisResSchema)[200]>;

//////////////////////////
// Save user rating
export const saveArticleUserRatingReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    userRating: contentSchema.shape.userRating,
  }),
});
export type SaveArticleUserRatingReq = z.infer<typeof saveArticleUserRatingReqSchema>;

export const saveArticleUserRatingResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type SaveArticleUserRatingRes = z.infer<(typeof saveArticleUserRatingResSchema)[200]>;

//////////////////////////
// Retry content analysis
export const retryArticleAnalysisReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type RetryArticleAnalysisReq = z.infer<typeof retryArticleAnalysisReqSchema>;

export const retryArticleAnalysisResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type RetryArticleAnalysisRes = z.infer<(typeof retryArticleAnalysisResSchema)[200]>;

//////////////////////////
// Export
export const exportArticleReqSchema = z.object({
  params: z.object({
    id: contentSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      kind: z.enum([ExportKind.Text, ExportKind.HTML, ExportKind.Markdown]),
      isDownload: z.boolean(),
    })
    .nullish(),
});
export type ExportArticleReq = z.infer<typeof exportArticleReqSchema>;

export const exportArticleResSchema = {
  200: z.object({
    data: z.string(),
  }),
};
export type ExportArticleRes = z.infer<(typeof exportArticleResSchema)[200]>;

//////////////////////////
// Export to integration
export const exportArticleIntegrationReqSchema = z.object({
  params: z.object({ id: contentSchema.shape._id }),
  body: z.discriminatedUnion('provider', [
    z.object({
      provider: z.literal(IntegrationProvider.Wordpress),
      integrationId: mongoIdSchema,
      accessUser: z.string().min(1),
      slug: z.string().optional(),
      status: z.enum(['publish', 'draft', 'pending', 'private', 'future']).optional(),
    }),
    z.object({
      provider: z.literal(IntegrationProvider.Shopify),
      integrationId: mongoIdSchema,
      blogId: z.string(),
      author: z.string().optional(),
      articleHandle: z.string().optional(),
      visibility: z.enum(['visible', 'hidden']).optional(),
    }),
    z.object({
      provider: z.literal(IntegrationProvider.Bloggle),
      integrationId: mongoIdSchema,
    }),
  ]),
});
export type ExportArticleIntegrationReq = z.infer<typeof exportArticleIntegrationReqSchema>;

export const exportArticleIntegrationResSchema = {
  200: z.object({ data: z.object({ externalId: z.union([z.string(), z.number()]) }) }),
};
export type ExportArticleIntegrationRes = z.infer<(typeof exportArticleIntegrationResSchema)[200]>;

//////////////////////////
// Generate primary keyword
export const generatePrimaryKeywordReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    brandId: brandSchema.shape._id,
    topic: z.string().min(3),
    previousKeywords: z.string().nullish(),
    language: z.string().nullish(),
    country: z.string().nullish(),
  }),
});
export type GeneratePrimaryKeywordReq = z.infer<typeof generatePrimaryKeywordReqSchema>;

export const generatePrimaryKeywordResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type GeneratePrimaryKeywordRes = z.infer<(typeof generatePrimaryKeywordResSchema)[200]>;

export const generatePrimaryKeywordSocketResSchema = z.object({
  contentId: z.string(),
  result: z
    .object({
      primaryKeywordChoices: z.array(
        z.object({
          difficulty: z.number(),
          primaryKeyword: z.string(),
          volume: z.number(),
        })
      ),
    })
    .or(
      z.object({
        generatedPrimaryKeyword: z.string(),
      })
    )
    .nullish(),
  status: aiStatusSchema,
  error: z.string(),
});
export type GeneratePrimaryKeywordSocketRes = z.infer<typeof generatePrimaryKeywordSocketResSchema>;

//////////////////////////
// Generate primary prompt
export const generatePrimaryPromptReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    brandId: brandSchema.shape._id,
    country: z.string().nullish(),
    language: z.string().nullish(),
    primaryKeyword: z.string().min(1),
    topic: z.string().min(3),
  }),
});
export type GeneratePrimaryPromptReq = z.infer<typeof generatePrimaryPromptReqSchema>;

export const generatePrimaryPromptResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type GeneratePrimaryPromptRes = z.infer<(typeof generatePrimaryPromptResSchema)[200]>;

export const generatePrimaryPromptSocketResSchema = z.object({
  contentId: z.string(),
  result: z
    .object({
      generatedPrimaryPrompt: z.string(),
    })
    .nullish(),
  status: aiStatusSchema,
  error: z.string(),
});
export type GeneratePrimaryPromptSocketRes = z.infer<typeof generatePrimaryPromptSocketResSchema>;

//////////////////////////
// Generate one click
export const generateOneClickReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    brandId: brandSchema.shape._id,
    topic: z.string().nullish(),
    keyword: z.string().min(1),
    primaryPrompt: z.string().min(1),
    country: z.string().nullish(),
    language: z.string().nullish(),
  }),
});
export type GenerateOneClickReq = z.infer<typeof generateOneClickReqSchema>;

export const generateOneClickResSchema = {
  200: z.object({
    data: z.object({
      analysis: contentAnalysisSchema.nullish(),
      content: contentSchema,
      funnel: contentFunnelSchema.nullish(),
      seoScore: seoResSchema,
    }),
  }),
};
export type GenerateOneClickRes = z.infer<(typeof generateOneClickResSchema)[200]>;

//////////////////////////
// Refresh content
export const refreshContentReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    brandId: brandSchema.shape._id,
    country: z.string().nullish(),
    language: z.string().nullish(),

    mode: z.string(),
    chosenTitle: z.string(),
    extraInformation: z.string(),
    numberOfResults: z.number(),
    primaryPrompt: z.string(),
    primaryKeyword: z.string(),
    targetWordCount: z.number().or(z.string()),
    tiptapJsonContent: z.record(z.any()).nullish(),
    url: z.string().nullish(),
  }),
});
export type RefreshContentReq = z.infer<typeof refreshContentReqSchema>;

export const refreshContentResSchema = {
  200: z.object({
    contentId: z.string(),
  }),
};
export type RefreshContentRes = z.infer<(typeof refreshContentResSchema)[200]>;
